import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { ClassTypeLabel } from '../../../components/classes/ClassTypeLabel';
import type { UserClassInterface } from '../../../types/userClass.interface';
import type { ClassInterface } from '../../../types/class.interface';
import LiveStreamJoinButton from '../../../components/my-classes/LiveStreamJoinButton';

interface UserScheduleProps {
  userClasses: UserClassInterface[];
  onCancel: (userClass: UserClassInterface) => void;
}

const formatClassDate = (timeStamp?: number, startTime?: string) => {
  if (!timeStamp && !startTime) return { day: 'N/A', time: 'N/A' };
  try {
    let date: Date;
    if (timeStamp) {
      date = new Date(timeStamp);
    } else if (startTime) {
      date = new Date(startTime);
    } else {
      return { day: 'N/A', time: 'N/A' };
    }
    if (isNaN(date.getTime())) {
      return { day: 'N/A', time: 'N/A' };
    }
    return {
      day: format(date, 'EEEE d', { locale: es }),
      time: format(date, 'hh:mm a', { locale: es })
    };
  } catch {
    return { day: 'N/A', time: 'N/A' };
  }
};

export function UserSchedule({ userClasses, onCancel }: UserScheduleProps) {
  return (
    <div className="w-full lg:w-1/2 pl-0 pr-0 lg:pr-2 mb-3 lg:mb-0" style={{ maxHeight: '267px', overflow: 'auto' }}>
      <div className="w-full">
        {userClasses && userClasses.length > 0 ? (
          userClasses.map((userClass, idx) => {
            const classData = userClass.class as unknown as ClassInterface | undefined;
            if (!classData) return null;

            const isUpcoming = new Date(userClass.startTime) > new Date() && !userClass.cancelled && !userClass.attended;
            const hasNotEnded = new Date(userClass.endTime) > new Date() && !userClass.cancelled;
            const { day, time } = formatClassDate(classData.timeStamp, userClass.startTime);

            const handleCancel = (e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              onCancel(userClass);
            };

            // Build normalized timestamp for class details URL
            const timeForLink = (() => {
              if (typeof classData.timeStamp === 'number' && !isNaN(classData.timeStamp)) {
                return classData.timeStamp;
              }
              if (userClass.startTime) {
                const d = new Date(userClass.startTime);
                if (!isNaN(d.getTime())) {
                  d.setSeconds(0, 0); // normalize to :00.000
                  return d.getTime();
                }
              }
              return '';
            })();

            return (
              <div key={idx} className="bg-white rounded-lg shadow-sm p-3 mb-2" style={{ minHeight: '81px' }}>
                <Link
                  to={`/clases/${classData.studio?.slug || 'default'}/${classData.slug || 'default'}/${timeForLink}`}
                  className="flex"
                >
                  <div className="flex flex-col justify-center pr-3" style={{ width: '30%' }}>
                    <div className="mb-2">
                      <ClassTypeLabel liveStreamClass={!!classData.liveStreamClass} />
                    </div>
                    <div className="font-semibold text-sm">
                      <div className="capitalize">{day}</div>
                      <div>{time}</div>
                    </div>
                  </div>
                  <div className="flex-grow flex items-center ml-3">
                    <div>
                      <div className="font-semibold text-sm capitalize mb-1">{classData.title}</div>
                      <h6 className="text-gray-500 text-xs mb-2">{classData.studio?.name || 'Studio'}</h6>
                      {userClass?.userClassHash?.classHash && !userClass?.class?.liveStreamClass && <div className="text-xs font-medium mb-2">
                        Tu código secreto: <span className="uppercase font-semibold">{userClass.userClassHash.classHash}</span>
                      </div>}
                      {isUpcoming && <div className="flex items-center lg:hidden">
                        <button
                          onClick={handleCancel}
                          className="bg-red-500 hover:bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded uppercase cursor-pointer"
                        >
                          Cancelar
                        </button>
                      </div>}
                    </div>
                  </div>
                  {isUpcoming && <div className="hidden lg:flex items-center">
                    <button
                      onClick={handleCancel}
                      className="bg-red-500 hover:bg-red-600 text-white text-xs font-semibold px-4 py-1 rounded uppercase cursor-pointer"
                      style={{ width: '100px' }}
                    >
                      Cancelar
                    </button>
                  </div>}
                </Link>

                {hasNotEnded && userClass?.class?.liveStreamClass && <LiveStreamJoinButton
                  userClass={userClass}
                />}
              </div>
            );
          })
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-3 mb-2">
            <div className="py-2 flex">
              <div className="flex items-center pr-5">
                <img
                  src="https://cache.beholos.com/img/fitness-pass-v2/global/calendar-icon.svg"
                  alt="Calendar icon"
                  className="w-8 h-8"
                />
              </div>
              <div className="flex-grow flex items-center">
                <div>
                  <div className="text-sm font-black leading-tight">No tienes clases reservadas</div>
                  <div className="text-xs text-gray-500 leading-tight mt-1">
                    Para empezar, revisa nuestras experiencias y reserva una actividad
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


